import React, {useEffect, useState} from 'react';
import PersonForm from './PersonForm';
import '../styles/TaskList.css';
import useApiStable from '../utils/useApiStable';

function PersonList({}) {
    const [persons, setPersons] = useState([]);
    const [search, setSearch] = useState('');
    const [editingPersonId, setEditingPersonId] = useState(null);
    const [showNewForm, setShowNewForm] = useState(false);
    const [error, setError] = useState('');
    const [importSuccess, setImportSuccess] = useState('');
    const [importErrors, setImportErrors] = useState([]);
    const [sortBy, setSortBy] = useState('name');
    const api = useApiStable();

    const formatRegisteredAt = (value) => {
        if (!value) return null;
        const d = new Date(value);
        if (isNaN(d.getTime())) return null;
        return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    const formatLastLogin = (value) => {
        if (!value) return null;
        const d = new Date(value);
        if (isNaN(d.getTime())) return null;
        const datePart = d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
        const timePart = d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
        return `${datePart}, ${timePart}`;
    };

    const loadPersons = () => {
        setError('');
        api.get('/persons')
            .then(data => setPersons(Array.isArray(data) ? data : []))
            .catch(err => setError(err.message || 'Fehler beim Laden der Personen'));
    };

    useEffect(() => {
        loadPersons();
    }, []);

    const handleEdit = (person) => {
        setShowNewForm(false);
        setEditingPersonId(prev => prev === person.id ? null : person.id);
    };

    const handleCreate = () => {
        setEditingPersonId(null);
        setShowNewForm(true);
    };

    const handleDelete = (person) => {
        const label = person.name || person.email || 'diese Person';
        if (window.confirm(`Person "${label}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`)) {
            api.delete(`/persons/${person.id}`)
                .then(() => loadPersons())
                .catch(err => setError(err.message || 'Fehler beim Löschen'));
        }
    };

    const handleActivate = (person) => {
        const label = person.name || person.email || 'diese Person';
        if (!window.confirm(`Account von "${label}" ohne E-Mail-Bestätigung aktivieren?`)) {
            return;
        }
        setError('');
        api.post(`/persons/${person.id}/activate`)
            .then(() => loadPersons())
            .catch(err => setError(err.message || 'Fehler beim Aktivieren des Accounts'));
    };

    const handleFormClose = (refresh = false) => {
        setEditingPersonId(null);
        setShowNewForm(false);
        if (refresh) {
            loadPersons();
        }
    };

    const handleExportCsv = async () => {
        try {
            const response = await api.fetchRaw('/persons/export/csv', { method: 'GET' });
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `personen_export_${new Date().toISOString().split('T')[0]}.csv`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            setError(err.message || 'CSV-Export fehlgeschlagen');
        }
    };

    const handleImportCsv = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        setError('');
        setImportSuccess('');
        setImportErrors([]);

        const formData = new FormData();
        formData.append('file', file);

        try {
            const response = await api.fetchRaw('/persons/import/csv', { method: 'POST', body: formData });
            const result = await response.json();

            if (result.hasErrors) {
                setImportSuccess(`${result.successCount} Personen erfolgreich importiert.`);
                setImportErrors(result.errors);
            } else {
                setImportSuccess(`${result.successCount} Personen erfolgreich importiert!`);
            }
            loadPersons();
        } catch (err) {
            setError(err.message || 'CSV-Import fehlgeschlagen');
        }

        event.target.value = '';
    };

    const searchLower = search.toLowerCase();
    const filtered = persons
        .slice()
        .sort((a, b) => {
            if (sortBy === 'createdAt') {
                const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
                const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
                return tb - ta;
            }
            if (sortBy === 'lastLoginAt') {
                const ta = a.lastLoginAt ? new Date(a.lastLoginAt).getTime() : 0;
                const tb = b.lastLoginAt ? new Date(b.lastLoginAt).getTime() : 0;
                return tb - ta;
            }
            return (a.name || '').localeCompare(b.name || '', 'de');
        })
        .filter(p =>
            !search ||
            (p.name || '').toLowerCase().includes(searchLower) ||
            (p.email || '').toLowerCase().includes(searchLower)
        );

    return (
        <div className="tasklist-container">
            <div className="tasklist-header">
                <h2>Personen</h2>
                <div style={{display: 'flex', gap: '8px'}}>
                    <button className="tasklist-btn" onClick={handleExportCsv}
                            style={{background: '#2196F3'}} title="Personen als CSV exportieren">
                        📥 CSV Export
                    </button>
                    <label className="tasklist-btn" style={{background: '#FF9800', cursor: 'pointer'}}
                           title="Personen aus CSV importieren">
                        📤 CSV Import
                        <input type="file" accept=".csv" onChange={handleImportCsv} style={{display: 'none'}}/>
                    </label>
                    <button className="tasklist-btn" onClick={handleCreate}>Neue Person</button>
                </div>
            </div>

            <div style={{display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px', fontSize: '0.9em'}}>
                <label htmlFor="person-sort" style={{color: '#666'}}>Sortieren:</label>
                <select
                    id="person-sort"
                    value={sortBy}
                    onChange={e => setSortBy(e.target.value)}
                    style={{padding: '4px 8px', border: '1px solid #ddd', borderRadius: '4px'}}
                >
                    <option value="name">Name</option>
                    <option value="createdAt">Registriert (neueste zuerst)</option>
                    <option value="lastLoginAt">Letzter Login (neueste zuerst)</option>
                </select>
            </div>

            <input
                type="text"
                placeholder="Suche nach Name oder E-Mail …"
                value={search}
                onChange={e => setSearch(e.target.value)}
                style={{
                    width: '100%',
                    padding: '8px 12px',
                    marginBottom: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '0.95em',
                    boxSizing: 'border-box'
                }}
            />

            {error && <div style={{color: '#c62828', fontSize: '0.85em', marginBottom: '8px', whiteSpace: 'pre-line'}}>{error}</div>}
            {importSuccess && <div style={{color: '#2e7d32', fontSize: '0.85em', marginBottom: '8px'}}>{importSuccess}</div>}
            {importErrors.length > 0 && (
                <div style={{
                    background: '#fff3e0',
                    border: '1px solid #ff9800',
                    borderRadius: '4px',
                    padding: '12px',
                    marginBottom: '12px',
                    maxHeight: '200px',
                    overflowY: 'auto'
                }}>
                    <strong style={{color: '#e65100'}}>Import-Fehler ({importErrors.length}):</strong>
                    <ul style={{margin: '8px 0 0 0', paddingLeft: '20px', fontSize: '0.85em'}}>
                        {importErrors.map((err, index) => (
                            <li key={index} style={{marginBottom: '4px', color: '#bf360c'}}>{err}</li>
                        ))}
                    </ul>
                </div>
            )}

            {showNewForm && (
                <div style={{marginBottom: '12px'}}>
                    <PersonForm person={null} onClose={handleFormClose}/>
                </div>
            )}

            <div style={{overflowX: 'auto'}}>
                <table style={{width: '100%', borderCollapse: 'collapse', fontSize: '0.95em'}}>
                    <thead>
                        <tr style={{background: '#f5f5f5', textAlign: 'left'}}>
                            <th style={{padding: '8px 12px'}}>Name</th>
                            <th style={{padding: '8px 12px'}}>E-Mail</th>
                            <th style={{padding: '8px 12px'}}>Status</th>
                            <th style={{padding: '8px 12px'}}>Registriert am</th>
                            <th style={{padding: '8px 12px'}}>Last Login</th>
                            <th style={{padding: '8px 12px'}}>Aktionen</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.map(person => (
                            <React.Fragment key={person.id}>
                                <tr style={{borderBottom: editingPersonId === person.id ? 'none' : '1px solid #eee'}}>
                                    <td style={{padding: '8px 12px'}}><strong>{person.name}</strong></td>
                                    <td style={{padding: '8px 12px', color: '#666'}}>{person.email}</td>
                                    <td style={{padding: '8px 12px'}}>
                                        {person.enabled === false && (
                                            <span style={{
                                                fontSize: '0.85em',
                                                color: '#e65100',
                                                background: '#fff3e0',
                                                border: '1px solid #ff9800',
                                                borderRadius: '4px',
                                                padding: '1px 6px'
                                            }}>
                                                ⏳ Nicht aktiviert
                                            </span>
                                        )}
                                    </td>
                                    <td style={{padding: '8px 12px', color: '#888', fontSize: '0.9em'}}>
                                        {formatRegisteredAt(person.createdAt) || '–'}
                                    </td>
                                    <td style={{padding: '8px 12px', color: formatLastLogin(person.lastLoginAt) ? '#888' : '#999', fontSize: '0.9em'}}>
                                        {formatLastLogin(person.lastLoginAt) || '–'}
                                    </td>
                                    <td style={{padding: '8px 12px'}}>
                                        <div style={{display: 'flex', gap: '6px', flexWrap: 'wrap'}}>
                                            {person.enabled === false && (
                                                <button className="tasklist-edit-btn" style={{background: '#43a047'}}
                                                        onClick={() => handleActivate(person)}
                                                        title="Account ohne E-Mail-Bestätigung aktivieren">
                                                    Aktivieren
                                                </button>
                                            )}
                                            <button className="tasklist-edit-btn"
                                                    onClick={() => handleEdit(person)}
                                                    style={{background: editingPersonId === person.id ? '#aaa' : undefined}}>
                                                {editingPersonId === person.id ? 'Schließen' : 'Bearbeiten'}
                                            </button>
                                            <button className="tasklist-edit-btn" style={{background: '#aaa'}}
                                                    onClick={() => handleDelete(person)}>Löschen
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                                {editingPersonId === person.id && (
                                    <tr>
                                        <td colSpan={6} style={{padding: '0 0 8px 0', borderBottom: '1px solid #eee'}}>
                                            <PersonForm person={person} onClose={handleFormClose}/>
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default PersonList;
